import Decimal from "decimal.js";
import {
  TroubleType,
  type TroubleData,
  type TroubleEntry,
} from "@erp/shared-types";
import { serializeMoney, tryParseMoney, type MoneyInput } from "./money";

/**
 * One invoice line as the footing check sees it. `totalPrice` is the extended
 * amount (quantity × unit price), which is before that line's own tax.
 */
export interface InvoiceLineTotalLine {
  totalPrice?: MoneyInput | null;
  taxAmount?: MoneyInput | null;
}

/**
 * Whether the lines foot to the document total.
 *
 * The document total includes tax. Line extensions usually do not, so tax is
 * counted once, on top of the extensions:
 * - header `taxAmount` when it is non-zero, otherwise
 * - the sum of per-line `taxAmount`.
 *
 * The amounts agree when the line extensions equal the document total (tax is
 * already inside the lines, or there is no tax) or when the extensions plus
 * that one tax figure equal it. A header tax that is also present as its own
 * line still agrees, because the extensions already equal the total.
 */
export interface InvoiceLineTotalAssessment {
  addsUp: boolean;
  /** Sum of line extensions, canonical 2-decimal string. Null when a line amount cannot be read. */
  lineTotal: string | null;
  /** Tax counted on top of the extensions. Null when it cannot be read. */
  countedTax: string | null;
  /** Document total, canonical 2-decimal string. Null when it cannot be read. */
  documentTotal: string | null;
}

function isBlank(value: MoneyInput | null | undefined): boolean {
  return value == null || (typeof value === "string" && value.trim() === "");
}

/** Blank becomes `blankValue`. Anything else must parse, or the read fails. */
function readAmount(
  value: MoneyInput | null | undefined,
  blankValue: Decimal | null,
): Decimal | null {
  if (isBlank(value)) return blankValue;
  return tryParseMoney(value);
}

const UNREADABLE: InvoiceLineTotalAssessment = {
  addsUp: false,
  lineTotal: null,
  countedTax: null,
  documentTotal: null,
};

/**
 * Compare invoice line extensions to a stated total, counting tax once.
 * Amounts that cannot be read are reported as not adding up. Approval does
 * not use this result.
 */
export function assessInvoiceLineTotal(input: {
  totalAmount: MoneyInput | null | undefined;
  taxAmount?: MoneyInput | null;
  lineItems: readonly InvoiceLineTotalLine[];
}): InvoiceLineTotalAssessment {
  const document = readAmount(input.totalAmount, null);
  const documentTotal = document ? serializeMoney(document) : null;
  if (!document || documentTotal == null) {
    return { ...UNREADABLE, documentTotal };
  }

  let lineSum = new Decimal(0);
  let lineTaxSum = new Decimal(0);
  for (const line of input.lineItems) {
    const price = readAmount(line.totalPrice, null);
    const lineTax = readAmount(line.taxAmount, new Decimal(0));
    if (!price || !lineTax) return { ...UNREADABLE, documentTotal };
    lineSum = lineSum.plus(price);
    lineTaxSum = lineTaxSum.plus(lineTax);
  }

  const headerTax = readAmount(input.taxAmount, new Decimal(0));
  if (!headerTax) return { ...UNREADABLE, documentTotal };

  // Header tax wins when it is set, so a rollup of the same line taxes is
  // not added a second time. Zero header tax falls through to line taxes.
  const counted = headerTax.isZero() ? lineTaxSum : headerTax;
  const lineTotal = serializeMoney(lineSum);
  const countedTax = serializeMoney(counted);
  const withTax = serializeMoney(lineSum.plus(counted));

  return {
    addsUp: lineTotal === documentTotal || withTax === documentTotal,
    lineTotal,
    countedTax,
    documentTotal,
  };
}

const LINE_TOTAL_MISMATCH_COMMENT =
  /^lineTotal=(\S+) countedTax=(\S+) documentTotal=(\S+) currency=(\S+)$/;

/** Stable comment stored on the trouble flag so the badge can translate it. */
export function formatLineTotalMismatchComment(parts: {
  lineTotal: string;
  countedTax: string;
  documentTotal: string;
  currency: string;
}): string {
  return `lineTotal=${parts.lineTotal} countedTax=${parts.countedTax} documentTotal=${parts.documentTotal} currency=${parts.currency}`;
}

/**
 * Pure update of `troubleData` for the line-total flag. Other trouble entries
 * are left in place. Returns null when the flag is clear and nothing else
 * remains, so callers do not persist an empty array.
 */
export function computeLineTotalMismatchTroubleData(
  current: TroubleData,
  assessment: InvoiceLineTotalAssessment,
  currency?: string | null,
): TroubleData {
  const others = (current ?? []).filter(
    (entry) => entry.type !== TroubleType.LINE_TOTAL_MISMATCH,
  );

  if (assessment.addsUp) {
    return others.length > 0 ? others : null;
  }

  const entry: TroubleEntry = {
    active: true,
    type: TroubleType.LINE_TOTAL_MISMATCH,
    comments: [
      formatLineTotalMismatchComment({
        lineTotal: assessment.lineTotal ?? "?",
        countedTax: assessment.countedTax ?? "?",
        documentTotal: assessment.documentTotal ?? "?",
        currency: currency?.trim() || "USD",
      }),
    ],
  };

  return [...others, entry];
}

export function parseLineTotalMismatchComment(comment: string): {
  lineTotal: string;
  countedTax: string;
  documentTotal: string;
  currency: string;
} | null {
  const match = LINE_TOTAL_MISMATCH_COMMENT.exec(comment.trim());
  if (!match) return null;
  const lineTotal = match[1];
  const countedTax = match[2];
  const documentTotal = match[3];
  const currency = match[4];
  if (!lineTotal || !countedTax || !documentTotal || !currency) return null;
  return { lineTotal, countedTax, documentTotal, currency };
}
